// material
import { Container } from '@mui/material';
// hooks
import useSettings from '../../hooks/useSettings';
// components
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { PATH_DASHBOARD } from '../../routes/paths';
import { checkRoles, ROLE_USER } from 'guards/CheckRoles';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../utils/axios';
import { useEffect, useState } from 'react';
import DeviceGroupForm from '../../components/device-groups/DeviceGroupForm';
import { MIconButton } from '../../components/@material-extend';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import { useSnackbar } from 'notistack';
import LoadingSpinner from '../../components/LoadingSpinner';
import { getProfile } from '../../redux/slices/user';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { Building, DeviceTag } from '../../@types/model/user';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { buildingState } from 'recoil/buildingState';
import BuildingForm from 'components/building/BuildingForm';

// ----------------------------------------------------------------------

export default function EditBuilding() {
  const { themeStretch } = useSettings();
  const { t } = useTranslation(['translation', 'custom']);
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const applicationDispatch = useDispatch();
  const { loaded, user } = useSelector((state: RootState) => state.user);
  const [building, setBuilding] = useState<Building | null>(null);
  const { guid } = useParams();
  const [saving, setSaving] = useState<boolean>(false);
  const buildings: Building[] = useRecoilValue(buildingState);
  const setBuildings = useSetRecoilState(buildingState);
  const [loading, setLoading] = useState<boolean>(false);
  const role = checkRoles(user, ROLE_USER) ? '/' : '/end-user';
  const { workspace } = useSelector((state: RootState) => state.workspace);

  useEffect(() => {
    setBuilding(buildings.find((b: Building) => b.guid === guid)!);
  }, []);

  const saveBuilding = (building: Building) => {
    setLoading(true);
    axios
      .put(`/api/v2/device-tag/${building.guid}`, {
        ...building,
        attributes: {
          ...building.attributes,
          name: building.attributes.name
        }
      })
      .then(() => {
        setLoading(false);
        setBuildings(
          buildings.map((b: Building) => {
            if (b.guid === building.guid) {
              return {
                ...building,
                attributes: {
                  ...building.attributes,
                  name: building.attributes.name
                }
              };
            } else return b;
          })
        );
        enqueueSnackbar(t('building_updated'), {
          variant: 'success',
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });

        applicationDispatch(getProfile());
        navigate(-1);
      })
      .catch(() => {
        setLoading(false);
      });
  };
  const pageTitle = t('edit_building');
  return (
    <Page title={pageTitle}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          heading={pageTitle}
          links={[
            { name: t('home'), href: role },
            {
              name: workspace?.wsName === 'avia' ? t('naves') : t('buildings', { ns: 'custom' }),
              href: PATH_DASHBOARD.buildings.list
            },
            { name: pageTitle, href: PATH_DASHBOARD.buildings.new }
          ]}
        />
        {!loaded || !building ? (
          <LoadingSpinner />
        ) : (
          <BuildingForm
            loading={loading}
            building={building}
            edit={true}
            submitHandler={saveBuilding}
          />
        )}
      </Container>
    </Page>
  );
}
